import type { FC } from 'react'
import { useMemo, useState } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box } from '@mui/material'
import type { FileUpdate } from '@/infra/sse/conversation/payloads'
import { CollapsibleContent } from './CollapsibleContent'
import { ToolDiffBlock } from './ToolDiffBlock'
import { useLazyEditContent } from './useLazyContent'

type EditToolContentProps = PreToolEditProps | PostToolEditProps

interface PreToolEditProps {
  readonly mode: 'pre-tool'
  readonly oldString: string
  readonly newString: string
  readonly language: string
  readonly previewThreshold?: number
}

interface PostToolEditProps {
  readonly mode: 'post-tool'
  readonly updates: readonly FileUpdate[]
  readonly language: string
  readonly previewThreshold?: number
}

export const EditToolContent: FC<EditToolContentProps> = (props) => {
  const [expanded, setExpanded] = useState(false)
  const previewThreshold = props.previewThreshold ?? 30
  const isPreTool = props.mode === 'pre-tool'
  const language = props.language
  const updates = props.mode === 'post-tool' ? props.updates : undefined

  const lazyData = useLazyEditContent({
    oldString: isPreTool ? props.oldString : '',
    newString: isPreTool ? props.newString : '',
    expanded,
    previewThreshold,
  })

  const totalUpdateLines = useMemo(() => {
    if (!updates) return 0
    return updates.reduce((sum, hunk) => sum + hunk.lines.length, 0)
  }, [updates])

  const shouldShowMore = isPreTool ? lazyData.shouldShowMore : totalUpdateLines > previewThreshold

  const displayUpdates = useMemo(() => {
    if (!updates) return undefined
    if (expanded) return updates
    return truncateUpdates(updates, previewThreshold)
  }, [expanded, updates, previewThreshold])

  const diffBlock = useMemo(() => {
    if (isPreTool) {
      return (
        <ToolDiffBlock
          mode="pre-tool"
          oldString={lazyData.displayOldString}
          newString={lazyData.displayNewString}
          language={language}
        />
      )
    }
    if (displayUpdates) {
      return <ToolDiffBlock mode="post-tool" updates={displayUpdates} language={language} />
    }
    return null
  }, [isPreTool, lazyData.displayOldString, lazyData.displayNewString, language, displayUpdates])

  return (
    <Box sx={contentContainerSx}>
      <CollapsibleContent
        shouldCollapse={shouldShowMore}
        expanded={expanded}
        onExpandChange={setExpanded}
      >
        {diffBlock}
      </CollapsibleContent>
    </Box>
  )
}

function truncateUpdates(
  updates: readonly FileUpdate[],
  previewThreshold: number,
): readonly FileUpdate[] {
  let lineCount = 0
  const truncated: FileUpdate[] = []

  for (const hunk of updates) {
    if (lineCount >= previewThreshold) break

    const remainingLines = previewThreshold - lineCount
    if (hunk.lines.length <= remainingLines) {
      truncated.push(hunk)
      lineCount += hunk.lines.length
    } else {
      truncated.push({
        ...hunk,
        lines: hunk.lines.slice(0, remainingLines),
      })
      break
    }
  }

  return truncated
}

const contentContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1.5,
}
